<template>
    <div>
        <button class="btn btn-primary chat-fab background-default" @click="openChat">
            <i class="bi bi-chat-dots"></i>
        </button>

        <div v-if="isChatOpen" :class="['chat-screen', { 'chat-desktop': !isMobile }]">
            <div class="chat-header">
                <div class="d-flex align-items-center gap-2">
                    <i class="bi bi-cpu-fill"></i>
                    <strong>Dailypag Suporte</strong>
                </div>
                <button class="btn btn-sm btn-outline-secondary border-0" @click="isChatOpen = false">
                    <i class="bi bi-x-lg"></i>
                </button>
            </div>

            <div class="chat-body" ref="chatBody">
                <div v-for="(msg, i) in messages" :key="i" :class="msg.type === 'user' ? 'user' : 'bot'">

                    <div v-if="msg.type === 'bot' || msg.type === 'model'"
                        class="bubble bot-bubble text-sm leading-relaxed" v-html="formatMarkdown(msg.text)"></div>

                    <div v-else class="bubble user-bubble">
                        {{ msg.text }}
                    </div>
                </div>

                <div v-if="isLoading && !isStreaming" class="bot">
                </div>
            </div>
            <div v-if="isLoading && !isStreaming" class="bot">
                <div class="bubble bot-bubble typing-indicator">
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <span class="dot"></span>
                    <small class="ms-2 text-muted italic">Escrevendo...</small>
                </div>
            </div>

            <div class="chat-input">
                <input v-model="query" @keyup.enter="submitQuery" :disabled="isLoading"
                    placeholder="Digite sua mensagem...">
                <button class="send-btn" @click="submitQuery" :disabled="isLoading || !query">
                    <i v-if="!isLoading" class="bi bi-send-fill"></i>
                    <div v-else class="spinner-border spinner-border-sm text-primary" role="status"></div>
                </button>
            </div>
        </div>
    </div>
</template>

<script>

export default {
    data() {
        return {
            isChatOpen: false,
            isLoading: false,
            isStreaming: false,
            query: '',
            isMobile: window.innerWidth <= 768,
            messages: [],
            isHistoryLoading: false
        }
    },
    mounted() {
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            this.isMobile = window.innerWidth <= 768;
        },

        async openChat() {
            this.isChatOpen = true;
            if (this.messages.length === 0) {
                await this.loadHistory();
            }
            this.scrollToBottom();
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.chatBody;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },

        async submitQuery() {
            if (!this.query || this.isLoading) return;

            const userText = this.query;

            // 1. Adiciona a pergunta do usuário
            this.messages.push({ type: 'user', text: userText });
            this.query = '';

            // 2. Inicia estados: isLoading ativa o "Escrevendo..." no template
            this.isLoading = true;
            this.isStreaming = false;

            // Garante que o scroll desça para mostrar que a pergunta foi enviada
            this.scrollToBottom();

            // 3. Prepara a bolha vazia que o bot vai preencher
            const botMessageIndex = this.messages.push({ type: 'bot', text: '' }) - 1;

            try {
                const token = localStorage.getItem('access_token');
                const baseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api';

                const response = await fetch(`${baseUrl}/support/ask`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'text/event-stream',
                        'Authorization': token ? `Bearer ${token}` : ''
                    },
                    body: JSON.stringify({ question: userText })
                });

                if (response.status === 401) {
                    localStorage.removeItem('access_token');
                    window.location.href = '/login?expired=true';
                    return;
                }

                if (!response.ok) throw new Error("Server error");
                if (!response.body) throw new Error("No response body");

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let isDone = false;

                while (!isDone) {
                    const { done, value } = await reader.read();
                    isDone = done;

                    if (value) {
                        // 4. No primeiro pacote de dados, ativamos o Streaming
                        // Isso faz sumir o "Escrevendo..." e começar a mostrar o texto
                        if (!this.isStreaming) {
                            this.isStreaming = true;
                        }

                        const chunk = decoder.decode(value, { stream: true });
                        const lines = chunk.split('\n');

                        lines.forEach(line => {
                            const trimmedLine = line.trim();
                            if (trimmedLine.startsWith('data: ')) {
                                try {
                                    const rawJson = trimmedLine.replace('data: ', '');
                                    const payload = JSON.parse(rawJson);

                                    if (payload.text) {
                                        // Alimenta a bolha do bot em tempo real
                                        this.messages[botMessageIndex].text += payload.text;

                                        // ROLAGEM: Mantém o scroll no pé enquanto a IA digita
                                        this.scrollToBottom();
                                    }
                                } catch (e) {
                                    console.error("Erro ao processar JSON:", e);
                                }
                            }
                        });
                    }
                }
            } catch (error) {
                // Caso ocorra erro, preenchemos a mensagem inicial do bot com o erro
                this.messages[botMessageIndex].text = "Desculpe, a **Dailypag** está com dificuldade de conexão. Tente novamente em instantes.";
                console.error("Stream error:", error);
            } finally {
                // Reset de estados e scroll final para garantir visibilidade
                this.isLoading = false;
                this.isStreaming = false;
                this.scrollToBottom();
            }
        },

        formatMarkdown(text) {
            if (!text) return '';

            let formattedText = text
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                // Cabeçalhos
                .replace(/^### (.*$)/gim, '<h3 class="text-lg font-bold text-blue-700 mt-4 mb-2">$1</h3>')
                // Negrito
                .replace(/\*\*(.*?)\*\*/g, '<strong class="font-extrabold text-gray-900">$1</strong>')
                // Itálico
                .replace(/\*(.*?)\*/g, '<em class="italic text-gray-800">$1</em>')
                // Listas (O segredo aqui é garantir que cada item de lista tenha sua própria linha)
                .replace(/^\* (.*$)/gim, '<li class="ml-5 list-disc text-gray-700 mb-1">$1</li>');

            // IMPORTANTE: Transformar as quebras de linha em <br> DEPOIS de processar o Markdown
            return formattedText.split('\n').join('<br>');
        },

        async loadHistory() {
            this.isHistoryLoading = true;
            try {
                const token = localStorage.getItem('access_token');
                const baseUrl = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api';

                const response = await fetch(`${baseUrl}/v1/support/history`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                });

                if (response.ok) {
                    const history = await response.json();

                    if (history.length > 0) {
                        // Mapeamos o histórico do banco para o formato do chat
                        this.messages = history.map(msg => ({
                            type: msg.role, // 'user' ou 'model' (que seu front trata como 'bot')
                            text: msg.content
                        }));
                    } else {
                        // Se não houver histórico, exibe a saudação padrão
                        this.messages.push({
                            type: 'bot',
                            text: 'Olá! Eu sou o assistente inteligente da Dailypag. Como posso te ajudar hoje?'
                        });
                    }
                }
            } catch (error) {
                console.error("Erro ao carregar histórico:", error);
            } finally {
                this.isHistoryLoading = false;
                this.scrollToBottom();
            }
        }


    }
}
</script>

<style scoped>
/* O container principal do chat */
.chat-screen {
    width: 380px;
    /* Aumentamos a altura total do card */
    height: 700px;
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    position: fixed;
    bottom: 100px;
    right: 30px;
    z-index: 1000;
}

/* A área onde as mensagens aparecem */
.chat-body {
    flex: 1;
    /* Faz o corpo ocupar todo o espaço disponível entre o header e o input */
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.25rem;
    overflow-y: auto;
    /* Definimos um limite maior para o scroll */
    max-height: 600px;
    background-color: #ffffff;
}

/* Ajuste para Mobile */
@media (max-width: 768px) {
    .chat-screen {
        width: 100%;
        height: 100%;
        bottom: 0;
        right: 0;
        border-radius: 0;
    }

    .chat-body {
        max-height: calc(100vh - 120px);
        /* Desconta o header e o input */
    }
}


.typing-indicator {
    display: flex;
    align-items: center;
    padding: 8px 15px !important;
}

.dot {
    width: 4px;
    height: 4px;
    margin: 0 2px;
    background: #6b7280;
    /* gray-500 */
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out both;
}

.dot:nth-child(1) {
    animation-delay: -0.32s;
}

.dot:nth-child(2) {
    animation-delay: -0.16s;
}
.background-default{
    color:#fff;
    background: #561569;
    border: 1px solid #561569;
}
@keyframes bounce {

    0%,
    80%,
    100% {
        transform: scale(0);
    }

    40% {
        transform: scale(1.0);
    }
}

.bot-bubble:empty {
    display: none;
}

.chat-fab {
    position: fixed;
    bottom: 30px;
    right: 30px;
    z-index: 999;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex; 
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    cursor: pointer;
}
</style>